import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ChangeDetectorRef } from '@angular/core';
import { CorrispettiviReceiptsApi } from '@app/services/corrispettivi-receipts-api.service';
// Importa il componente di conferma specifico o usane uno generico
// import { ConfirmCorrispettiviUploadXmlComponent } from '@app/confirm-corrispettivi-upload-xml/confirm-corrispettivi-upload-xml.component';

@Component({
  selector: 'izy-upload-corrispettivi-xml',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    BackHeaderComponent,
  ],
  templateUrl: './upload-corrispettivi-xml.component.html',
  styleUrl: './upload-corrispettivi-xml.component.scss',
})
export class UploadCorrispettiviXmlComponent implements OnInit {
  @ViewChild('fileInput') fileInputVariable!: ElementRef;

  uploadForm: FormGroup;
  selectedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;
  uploadSuccess = false;
  xmlContent: string | null = null;

  // Campi estratti per la preview del corrispettivo
  deviceId: string = '';
  progressiveNumber: string = '';
  totalAmount: string = '';
  nDocs: string = '';
  closingDate: string = '';

  isDragOver = false;

  constructor(
    private fb: FormBuilder,
    private corrispettiviApi: CorrispettiviReceiptsApi,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {
    this.uploadForm = this.fb.group({
      organization_id: ['', [Validators.required, Validators.min(1)]],
    });
  }

  ngOnInit() {
    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );
    this.uploadForm.patchValue({ organization_id: organizationId });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  removeFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.xmlContent = null;
    this.uploadError = null;
    this.deviceId = '';
    this.progressiveNumber = '';
    this.totalAmount = '';
    this.nDocs = '';
    this.closingDate = '';
    if (this.fileInputVariable) {
      this.fileInputVariable.nativeElement.value = '';
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  private handleFileSelection(file: File): void {
    if ((file && file.type === 'text/xml') || file.name.endsWith('.xml')) {
      this.selectedFile = file;
      this.uploadError = null;
      this.readXmlFile(file);
    } else {
      this.uploadError =
        'Per favore seleziona un file XML valido (Dati Corrispettivi)';
      this.selectedFile = null;
    }
  }

  private readXmlFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.xmlContent = e.target.result;
      if (this.xmlContent) {
        this.parseCorrispettivoXml(this.xmlContent);
      }
    };
    reader.readAsText(file);
  }

  private parseCorrispettivoXml(xmlContent: string): void {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

      // Estrazione Matricola RT (IdDispositivo)
      const idDispositivo = xmlDoc.getElementsByTagName('IdDispositivo')[0];
      this.deviceId = idDispositivo ? idDispositivo.textContent || '' : 'N/A';

      // Estrazione Progressivo
      const progressivo = xmlDoc.getElementsByTagName('Progressivo')[0];
      this.progressiveNumber = progressivo
        ? progressivo.textContent || ''
        : 'N/A';

      // Estrazione Numero Documenti Commerciali
      const numeroDoc = xmlDoc.getElementsByTagName('NumeroDocCommerciali')[0];
      this.nDocs = numeroDoc ? numeroDoc.textContent || '0' : '0';

      // Estrazione Data di Emissione
      const dataEmissione = xmlDoc.getElementsByTagName('DataEmissione')[0];
      this.closingDate = dataEmissione?.textContent
        ? this.formatDateToItalian(dataEmissione.textContent)
        : '';

      // Estrazione Ammontare (Somma se ci sono più riepiloghi/aliquote)
      const ammontareElements = xmlDoc.getElementsByTagName('Ammontare');
      const ammontareDCElements =
        xmlDoc.getElementsByTagName('ImponibileVendite')[0];
      let DCTotal = '0';
      DCTotal = ammontareDCElements
        ? ammontareDCElements.textContent || '0'
        : '0';

      let total = 0;
      for (let i = 0; i < ammontareElements.length; i++) {
        total += parseFloat(ammontareElements[i].textContent || '0');
      }

      // add eventually DC total
      let finalTotal = 0;
      finalTotal = total + Number(DCTotal);
      this.totalAmount = finalTotal.toFixed(2).replace('.', ',');

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error parsing Corrispettivo XML:', error);
    }
  }

  onSubmit(): void {
    if (this.uploadForm.valid && this.selectedFile) {
      this.uploadCorrispettivo();
    }
  }

  private async uploadCorrispettivo(): Promise<void> {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadError = null;

    const organizationId = this.uploadForm.get('organization_id')?.value;

    this.corrispettiviApi
      .uploadCorrispettiviReceiptsXml(organizationId, this.selectedFile!)
      .subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round(
              (100 * event.loaded) / event.total,
            );
          } else if (event.type === HttpEventType.Response) {
            this.isUploading = false;
            this.uploadSuccess = true;
            this.selectedFile = null;
            this.xmlContent = null;
            this.uploadForm.reset({ organization_id: organizationId });
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.isUploading = false;
          this.uploadError =
            error.error?.message || "Errore durante l'invio del file RT.";
          this.cdr.detectChanges();
        },
      });
  }

  formatToItalian(value: string | number): string {
    if (value === null || value === undefined) return '';

    return value.toString().replace(/\./g, ',');
  }

  formatDateToItalian(isoDate: string): string {
    // Handles both YYYY-MM-DD and YYYY-MM-DDTHH:mm:ss
    const datePart = isoDate.split('T')[0];
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return isoDate;
    return `${day}/${month}/${year}`;
  }

  prepareNewUpload(): void {
    this.uploadSuccess = false;
    this.uploadError = null;
    const orgId = this.route.snapshot.paramMap.get('organizationId');
    this.uploadForm.patchValue({ organization_id: Number(orgId) });
  }

  navigateToCorrispettivi() {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/corrispettivi-receipts`]);
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }
}
